// Pure, framework-free helpers for the infrastructure monitoring section.
// Kept dependency-injected (health/cost functions are passed in) so they stay
// unit-testable without React and always reflect the SAME services the tiles
// render.

// Tally service health, monthly spend and alarms across the exact set of
// service keys that are actually rendered as tiles, so the summary strip counts
// always match what the user sees below it.
//
// getHealth(key) must return an object shaped like { status } where status is
// one of 'healthy' | 'warning' | 'critical' | 'unknown'. An 'unknown' status is
// the Partial-data state and is counted separately — never as healthy.
// getCost(key) must return a number (monthly $).
export function computeInfraSummary(accountData, renderedServiceKeys, getHealth, getCost) {
  const summary = {
    critical: 0,
    warning: 0,
    healthy: 0,
    partial: 0,
    totalMonthlySpend: 0,
    alarmsInAlarm: (accountData && accountData.alarms && accountData.alarms.alarm) || 0,
  };

  const keys = Array.isArray(renderedServiceKeys) ? renderedServiceKeys : [];
  for (const key of keys) {
    const status = (getHealth(key) || {}).status;
    switch (status) {
      case 'critical':
        summary.critical += 1;
        break;
      case 'warning':
        summary.warning += 1;
        break;
      case 'unknown':
        summary.partial += 1;
        break;
      default:
        // Treat any non-issue, non-partial status as healthy.
        summary.healthy += 1;
        break;
    }

    const cost = Number(getCost(key));
    if (Number.isFinite(cost)) {
      summary.totalMonthlySpend += cost;
    }
  }

  summary.totalMonthlySpend = Math.round(summary.totalMonthlySpend * 100) / 100;
  return summary;
}

// Decide whether a single service tile survives the search text + All/Issues
// filter. Pure so it can be unit-tested and reused by the render.
//
// - search: case-insensitive substring matched against both the service key and
//   its display name; empty/whitespace search matches everything.
// - filter: 'issues' keeps only services whose status is 'critical' or
//   'warning'; any other value ('all') keeps every status.
export function matchesInfraFilter(key, name, status, options) {
  const opts = options || {};
  const search = (opts.search || '').trim().toLowerCase();
  const filter = opts.filter || 'all';

  if (search) {
    const haystack = `${key || ''} ${name || ''}`.toLowerCase();
    if (!haystack.includes(search)) return false;
  }

  if (filter === 'issues') {
    return status === 'critical' || status === 'warning';
  }

  return true;
}
